import React, { useState } from 'react';
import { X, Play, Loader2 } from 'lucide-react';
import { useEnvironment } from '../contexts/EnvironmentContext';

/**
 * PolicyActivationModal Component
 *
 * Modal for activating a policy in a specific environment.
 * Styled to match PolicyWizard.
 */

const PolicyActivationModal = ({
  policy,
  isOpen,
  onClose,
  onActivate,
  apps = []
}) => {
  const { environments } = useEnvironment();
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [selectedApp, setSelectedApp] = useState('');
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !policy) return null;

  const handleActivate = async () => {
    if (!selectedEnvironment) {
      setError('Please select an environment');
      return;
    }

    setActivating(true);
    setError(null);

    try {
      await onActivate({
        policy,
        environment: selectedEnvironment,
        version: policy.latest_version || 1,
        app_id: selectedApp || undefined
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to activate policy');
    } finally {
      setActivating(false);
    }
  };

  const handleClose = () => {
    setSelectedEnvironment('');
    setSelectedApp('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background-secondary border border-border-subtle rounded-lg w-full max-w-md flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <Play size={18} className="text-status-success" />
            <h2 className="text-lg font-semibold text-text-primary">
              Activate Policy
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Policy Name */}
          <div className="p-4 bg-surface-overlay/50 rounded-lg border border-border-subtle">
            <div className="text-xs text-text-muted mb-1">Policy</div>
            <div className="font-medium text-text-primary">{policy.name}</div>
          </div>

          {/* Environment Selection */}
          <div>
            <label className="block text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-1.5">
              Environment <span className="text-red-400">*</span>
            </label>
            <select
              value={selectedEnvironment}
              onChange={(e) => setSelectedEnvironment(e.target.value)}
              className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary focus:outline-none focus:border-accent-primary-base"
              style={{ colorScheme: document.documentElement.getAttribute('data-color-mode') || 'dark' }}
            >
              <option value="" className="bg-background-tertiary text-text-primary">Select environment...</option>
              {environments.map((env) => (
                <option key={env.name} value={env.name} className="bg-background-tertiary text-text-primary">
                  {env.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted mt-1">
              The policy will be active in this environment
            </p>
          </div>

          {/* App Selection (Optional) */}
          {apps.length > 0 && (
            <div>
              <label className="block text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-1.5">
                App <span className="text-text-muted font-normal normal-case">(optional)</span>
              </label>
              <select
                value={selectedApp}
                onChange={(e) => setSelectedApp(e.target.value)}
                className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary focus:outline-none focus:border-accent-primary-base"
                style={{ colorScheme: document.documentElement.getAttribute('data-color-mode') || 'dark' }}
              >
                <option value="" className="bg-background-tertiary text-text-primary">All apps in environment</option>
                {apps.map((app) => (
                  <option key={app.app_id} value={app.app_id} className="bg-background-tertiary text-text-primary">
                    {app.app_name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-text-muted mt-1">
                Optionally limit to a specific app
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-status-danger/20 border border-status-danger/30 rounded-lg text-sm text-status-danger">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border-subtle bg-background-secondary">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleActivate}
            disabled={activating || !selectedEnvironment}
            className="flex items-center gap-2 px-4 py-2 bg-accent-primary-base text-background-primary rounded text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {activating ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Activating...
              </>
            ) : (
              <>
                <Play size={14} />
                Activate
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolicyActivationModal;
